import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Globe, ExternalLink, Copy, Check, Image, Type, Info, ChevronDown, ChevronUp, Smartphone, Monitor, Code, Camera, Award, MapPin, Briefcase, BarChart, MessageCircle, Clipboard, Zap, Palette, CheckCircle } from 'lucide-react';
import './BrandfetchSinglePageDisplay.css';

/**
 * BrandfetchSinglePageDisplay Component
 * Displays brand information in a single scrollable page layout
 * 
 * @param {Object} props - Component props
 * @param {string} props.domain - The domain to fetch data for
 * @param {Function} props.onClose - Function to call when closing the display
 * @param {Function} props.onSwitchView - Function to call when switching to tabbed view
 */
const BrandfetchSinglePageDisplay = ({ domain, onClose, onSwitchView }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [domainData, setDomainData] = useState(null);
    const [aiData, setAiData] = useState(null);
    const [brandData, setBrandData] = useState(null);
    const [dominantColors, setDominantColors] = useState(['#f0f0f0']);
    const [copiedColor, setCopiedColor] = useState(null);
    const [socialLinksWithOG, setSocialLinksWithOG] = useState([]);
    const [appIdeas, setAppIdeas] = useState([]);
    const [features, setFeatures] = useState([]);
    const [marketingTips, setMarketingTips] = useState([]);
    const [contentCategories, setContentCategories] = useState([]);
    const [brandAnalysis, setBrandAnalysis] = useState(null);
    const [colorSchemes, setColorSchemes] = useState([]);

    // Extract social links with useMemo to ensure it's defined before the useEffect that uses it
    const socialLinks = useMemo(() => {
        return brandData?.links?.map(link => ({
            type: link.name || 'website',
            url: link.url,
            username: link.username || null,
            followers: link.followers || null,
            verified: link.verified || false
        })) || [];
    }, [brandData]);

    // Define appLinks and industries using useMemo as well
    const appLinks = useMemo(() => brandData?.appLinks || [], [brandData]);
    const industries = useMemo(() => brandData?.industries || [], [brandData]);
    const companyInfo = useMemo(() => brandData?.analysis || {}, [brandData]);
    const screenshots = useMemo(() => brandData?.screenshots || {}, [brandData]);

    // Helper function to determine text color based on background
    const getTextColor = (bgColor) => {
        if (!bgColor) return '#000000';
        const rgb = bgColor.match(/\d+/g);
        if (!rgb) return '#000000';
        const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    useEffect(() => {
        if (domain) {
            fetchAllData();
        }
    }, [domain]);

    // Add color extraction from logo with better error handling
    useEffect(() => {
        const extractColors = async () => {
            if (!brandData?.logos?.length || !brandData.logos[0]?.formats?.length) {
                // Use default colors if no logo available
                setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                return;
            }

            try {
                // Find the first available logo format
                const logoUrl = brandData.logos[0].formats.find(format => format.src)?.src;
                
                if (!logoUrl) {
                    setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                    return;
                }
                
                // Create a new promise to handle image loading
                await new Promise((resolve) => {
                    const img = new Image();
                    
                    img.onload = () => {
                        // Simple color extraction - in a real app, you'd use a more sophisticated algorithm
                        // This is a placeholder for demonstration
                        const colors = ['#4CAF50', '#2196F3', '#9C27B0']; // Example colors
                        
                        // If the brand has theme colors, use those instead
                        if (brandData.colors && brandData.colors.length > 0) {
                            const brandColors = brandData.colors.map(color => color.hex);
                            setDominantColors(brandColors.length > 0 ? brandColors : colors);
                        } else {
                            setDominantColors(colors);
                        }
                        
                        resolve();
                    };
                    
                    img.onerror = () => {
                        console.error('Error loading image for color extraction');
                        // Use fallback colors
                        setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
                        resolve(); // Resolve anyway to continue
                    };
                    
                    // Set the src to start loading
                    img.src = logoUrl;
                });
            } catch (error) {
                console.error('Error in color extraction:', error);
                setDominantColors(['#f0f0f0', '#e0e0e0', '#d0d0d0']);
            }
        };
        
        extractColors();
    }, [brandData]);

    // Function to handle refresh button click
    const handleRefresh = () => {
        fetchAllData(true);
    };

    const fetchAllData = async (refresh = false) => {
        try {
            setLoading(true);
            setError(null);
            
            // Flag to prevent repeated requests for ai-data
            const aiDataChecked = {value: false};
            
            // Normalize domain
            const normalizedDomain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
            
            // Fetch brand data first
            const brandResponse = await fetch(`/api/website-data/brandfetch/${normalizedDomain}${refresh ? '?refresh=true' : ''}`);
            let brandResult = null;
            
            if (brandResponse.ok) {
                brandResult = await brandResponse.json();
                console.log('Fetched brand data:', brandResult); // Log data for debugging
                
                // Ensure we have at least a minimal structure
                if (!brandResult) {
                    brandResult = { name: normalizedDomain };
                }
                
                // Set brand data
                setBrandData(brandResult);
                
                // Check if we need to fetch domain data
                if (!brandResult || Object.keys(brandResult).length === 0) {
                    // Try to fetch domain data if brand data is empty
                    const domainResponse = await fetch(`/api/website-data/domain/${normalizedDomain}`);
                    if (domainResponse.ok) {
                        const domainResult = await domainResponse.json();
                        setDomainData(domainResult);
                    }
                }
            }
            
            // Prepare safe fetch function to handle 404 and other errors
            const safeFetch = async (url) => {
                try {
                    const response = await fetch(url);
                    if (response.ok) {
                        return await response.json();
                    }
                    
                    // Only log as a warning for 404s on optional data endpoints
                    if (response.status === 404) {
                        // These are optional endpoints, so return null silently without logging
                        return null;
                    }
                    
                    console.warn(`Failed to fetch ${url}: ${response.status}`);
                    return null;
                } catch (err) {
                    console.error(`Error fetching ${url}:`, err);
                    return null;
                }
            };
            
            // Fetch additional data with safe fetch
            const domainResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}`);
            const aiDataResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/ai-data`);
            const appIdeasResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/app-ideas`);
            const featuresResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/features`);
            const colorSchemesResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/color-schemes`);
            const marketingTipsResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/marketing-tips`);
            const contentCategoriesResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/content-categories`);
            const brandAnalysisResult = await safeFetch(`/api/website-data/domain/${normalizedDomain}/brand-analysis`);
            
            // Set state with results
            setDomainData(domainResult);
            
            // Process AI data if available
            if (aiDataResult) {
                const processedAiData = aiDataResult.data ? aiDataResult.data : aiDataResult;
                
                // Process insights to ensure they're safe to render
                if (processedAiData && processedAiData.insights) {
                    if (Array.isArray(processedAiData.insights)) {
                        processedAiData.insights = processedAiData.insights.map(insight => {
                            return typeof insight === 'object' ? JSON.stringify(insight) : insight;
                        });
                    } else if (typeof processedAiData.insights === 'object') {
                        // Convert each insight value to string if it's an object
                        Object.keys(processedAiData.insights).forEach(key => {
                            const value = processedAiData.insights[key];
                            if (typeof value === 'object' && value !== null) {
                                processedAiData.insights[key] = JSON.stringify(value);
                            }
                        });
                    }
                }
                
                // Process recommendations similarly
                if (processedAiData && processedAiData.recommendations && Array.isArray(processedAiData.recommendations)) {
                    processedAiData.recommendations = processedAiData.recommendations.map(rec => {
                        return typeof rec === 'object' ? JSON.stringify(rec) : rec;
                    });
                }
                
                setAiData(processedAiData);
            } else {
                setAiData(null);
            }
            
            setAppIdeas(appIdeasResult || []);
            setFeatures(featuresResult || []);
            setColorSchemes(colorSchemesResult || []);
            setMarketingTips(marketingTipsResult || []);
            setContentCategories(contentCategoriesResult || []);
            setBrandAnalysis(brandAnalysisResult || null);
            
            // Set loading to false
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load brand information. Please try again.');
            setLoading(false);
        }
    };

    // Make the ensureString function more robust
    const ensureString = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'boolean') return value.toString();
        if (Array.isArray(value)) return JSON.stringify(value);
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (e) {
                return '[Object]';
            }
        }
        return String(value);
    };

    // Fetch OpenGraph data for social links
    useEffect(() => {
        const fetchOpenGraphData = async () => {
            if (!socialLinks || socialLinks.length === 0) return;
            
            const updatedLinks = await Promise.all(
                socialLinks.map(async (link) => {
                    try {
                        const response = await fetch(`/api/website-data/og-data?url=${encodeURIComponent(link.url)}`);
                        if (response.ok) {
                            const ogData = await response.json();
                            return { ...link, ogData };
                        }
                        return link;
                    } catch (error) {
                        console.error(`Error fetching OG data for ${link.url}:`, error);
                        return link;
                    }
                })
            );
            
            setSocialLinksWithOG(updatedLinks);
        };
        
        fetchOpenGraphData();
    }, [socialLinks]);

    // Helper function to get social media icon
    const getSocialIcon = (platform) => {
        const platformLower = platform.toLowerCase();
        
        if (platformLower.includes('facebook')) return <i className="fab fa-facebook"></i>;
        if (platformLower.includes('twitter') || platformLower.includes('x.com')) return <i className="fab fa-twitter"></i>;
        if (platformLower.includes('instagram')) return <i className="fab fa-instagram"></i>;
        if (platformLower.includes('linkedin')) return <i className="fab fa-linkedin"></i>;
        if (platformLower.includes('youtube')) return <i className="fab fa-youtube"></i>;
        if (platformLower.includes('pinterest')) return <i className="fab fa-pinterest"></i>;
        if (platformLower.includes('tiktok')) return <i className="fab fa-tiktok"></i>;
        if (platformLower.includes('github')) return <i className="fab fa-github"></i>;
        if (platformLower.includes('medium')) return <i className="fab fa-medium"></i>;
        if (platformLower.includes('reddit')) return <i className="fab fa-reddit"></i>;
        if (platformLower.includes('snapchat')) return <i className="fab fa-snapchat"></i>;
        if (platformLower.includes('whatsapp')) return <i className="fab fa-whatsapp"></i>;
        if (platformLower.includes('telegram')) return <i className="fab fa-telegram"></i>;
        if (platformLower.includes('discord')) return <i className="fab fa-discord"></i>;
        if (platformLower.includes('slack')) return <i className="fab fa-slack"></i>;
        if (platformLower.includes('vimeo')) return <i className="fab fa-vimeo"></i>;
        if (platformLower.includes('twitch')) return <i className="fab fa-twitch"></i>;
        if (platformLower.includes('dribbble')) return <i className="fab fa-dribbble"></i>;
        if (platformLower.includes('behance')) return <i className="fab fa-behance"></i>;
        if (platformLower.includes('flickr')) return <i className="fab fa-flickr"></i>;
        if (platformLower.includes('soundcloud')) return <i className="fab fa-soundcloud"></i>;
        if (platformLower.includes('spotify')) return <i className="fab fa-spotify"></i>;
        if (platformLower.includes('tumblr')) return <i className="fab fa-tumblr"></i>;
        if (platformLower.includes('quora')) return <i className="fab fa-quora"></i>;
        if (platformLower.includes('vk')) return <i className="fab fa-vk"></i>;
        if (platformLower.includes('weibo')) return <i className="fab fa-weibo"></i>;
        if (platformLower.includes('line')) return <i className="fab fa-line"></i>;
        
        // Default icon for other platforms
        return <Globe size={16} />;
    };

    // Helper function to get social media brand color
    const getSocialColor = (platform) => {
        const platformLower = platform.toLowerCase();
        
        if (platformLower.includes('facebook')) return '#1877F2';
        if (platformLower.includes('twitter') || platformLower.includes('x.com')) return '#1DA1F2';
        if (platformLower.includes('instagram')) return '#E4405F';
        if (platformLower.includes('linkedin')) return '#0A66C2';
        if (platformLower.includes('youtube')) return '#FF0000';
        if (platformLower.includes('pinterest')) return '#BD081C';
        if (platformLower.includes('tiktok')) return '#000000';
        if (platformLower.includes('github')) return '#181717';
        if (platformLower.includes('medium')) return '#000000';
        if (platformLower.includes('reddit')) return '#FF4500';
        if (platformLower.includes('snapchat')) return '#FFFC00';
        if (platformLower.includes('whatsapp')) return '#25D366';
        if (platformLower.includes('telegram')) return '#26A5E4';
        if (platformLower.includes('discord')) return '#5865F2';
        if (platformLower.includes('slack')) return '#4A154B';
        if (platformLower.includes('vimeo')) return '#1AB7EA';
        if (platformLower.includes('twitch')) return '#9146FF';
        if (platformLower.includes('dribbble')) return '#EA4C89';
        if (platformLower.includes('behance')) return '#1769FF';
        if (platformLower.includes('flickr')) return '#FF0084';
        if (platformLower.includes('soundcloud')) return '#FF3300';
        if (platformLower.includes('spotify')) return '#1DB954';
        if (platformLower.includes('tumblr')) return '#36465D';
        if (platformLower.includes('quora')) return '#B92B27';
        if (platformLower.includes('vk')) return '#4C75A3';
        if (platformLower.includes('weibo')) return '#DF2029';
        if (platformLower.includes('line')) return '#00C300';
        
        // Default color for other platforms
        return '#6c757d';
    };

    // Helper function to format platform name
    const formatPlatformName = (platform) => {
        if (!platform) return 'Website';
        
        const platformLower = platform.toLowerCase();
        
        // Special cases
        if (platformLower === 'twitter' || platformLower === 'x' || platformLower.includes('x.com')) return 'X (Twitter)';
        if (platformLower === 'fb' || platformLower.includes('facebook')) return 'Facebook';
        if (platformLower === 'ig' || platformLower.includes('instagram')) return 'Instagram';
        if (platformLower === 'yt' || platformLower.includes('youtube')) return 'YouTube';
        if (platformLower === 'li' || platformLower.includes('linkedin')) return 'LinkedIn';
        if (platformLower === 'pin' || platformLower.includes('pinterest')) return 'Pinterest';
        if (platformLower === 'tt' || platformLower.includes('tiktok')) return 'TikTok';
        if (platformLower.includes('github')) return 'GitHub';
        if (platformLower.includes('medium')) return 'Medium';
        if (platformLower.includes('reddit')) return 'Reddit';
        if (platformLower.includes('snapchat')) return 'Snapchat';
        if (platformLower.includes('whatsapp')) return 'WhatsApp';
        if (platformLower.includes('telegram')) return 'Telegram';
        if (platformLower.includes('discord')) return 'Discord';
        if (platformLower.includes('slack')) return 'Slack';
        if (platformLower.includes('vimeo')) return 'Vimeo';
        if (platformLower.includes('twitch')) return 'Twitch';
        if (platformLower.includes('dribbble')) return 'Dribbble';
        if (platformLower.includes('behance')) return 'Behance';
        if (platformLower.includes('flickr')) return 'Flickr';
        if (platformLower.includes('soundcloud')) return 'SoundCloud';
        if (platformLower.includes('spotify')) return 'Spotify';
        if (platformLower.includes('tumblr')) return 'Tumblr';
        if (platformLower.includes('quora')) return 'Quora';
        if (platformLower.includes('vk')) return 'VK';
        if (platformLower.includes('weibo')) return 'Weibo';
        if (platformLower.includes('line')) return 'LINE';
        
        // Capitalize first letter of each word for other platforms
        return platform.replace(/\b\w/g, l => l.toUpperCase());
    };

    // Helper function to format follower count
    const formatFollowers = (count) => {
        if (!count) return '';
        
        if (count >= 1000000) {
            return `${(count / 1000000).toFixed(1)}M followers`;
        } else if (count >= 1000) {
            return `${(count / 1000).toFixed(1)}K followers`;
        } else {
            return `${count} followers`;
        }
    };

    // Helper function to get app store icon
    const getAppIcon = (platform) => {
        const platformLower = platform.toLowerCase();
        
        if (platformLower.includes('ios') || platformLower.includes('apple') || platformLower.includes('app store')) {
            return <i className="fab fa-app-store"></i>;
        }
        if (platformLower.includes('android') || platformLower.includes('google') || platformLower.includes('play store')) {
            return <i className="fab fa-google-play"></i>;
        }
        
        // Default icon
        return <Smartphone size={16} />;
    };

    // Generate gradient background style
    const gradientStyle = dominantColors.length > 0 ? {
        background: `linear-gradient(135deg, ${dominantColors.join(', ')})`,
        color: getTextColor(dominantColors[0])
    } : {};

    // Find a suitable banner image from screenshots if available
    const getBannerImage = () => {
        if (screenshots && Object.keys(screenshots).length > 0) {
            // Try to find a website screenshot first
            if (screenshots.website && screenshots.website.length > 0) {
                return screenshots.website[0].src;
            }
            
            // Otherwise use the first screenshot from any category
            for (const [type, images] of Object.entries(screenshots)) {
                if (images && images.length > 0) {
                    return images[0].src;
                }
            }
        }
        return null;
    };

    const bannerImage = getBannerImage();

    if (loading) {
        return (
            <div className="brandfetch-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="brandfetch-modal-content loading-container" onClick={(e) => e.stopPropagation()}>
                    <div className="loading-spinner"></div>
                    <p>Loading brand information...</p>
                </div>
            </div>
        );
    }

    if (error || !brandData) {
        return (
            <div className="brandfetch-modal" onClick={(e) => {
                e.stopPropagation();
                if (onClose) onClose();
            }}>
                <div className="brandfetch-modal-content error-container" onClick={(e) => e.stopPropagation()}>
                    <h2 className="modal-title">No Information Available</h2>
                    <div className="error-icon">⚠️</div>
                    <p>We couldn't find any information for {domain}.</p>
                    <button className="btn btn-primary" onClick={handleRefresh}>Try Again</button>
                </div>
            </div>
        );
    }

    const { name, description } = brandData || {};
    const { websiteData } = domainData || {};
    const { insights = {}, recommendations = [] } = aiData || {};
    
    return (
        <div className="brandfetch-modal" onClick={(e) => {
            e.stopPropagation();
            if (onClose) onClose();
        }}>
            <div className="brandfetch-single-page-modal" onClick={(e) => e.stopPropagation()}>
                {/* HEADER SECTION */}
                <div 
                    className="brandfetch-header-wrapper"
                    style={{
                        position: 'sticky',
                        top: 0,
                        width: '100%',
                        zIndex: 10
                    }}
                >
                    {/* Banner image backdrop */}
                    {bannerImage && (
                        <div className="brandfetch-banner">
                            <img 
                                src={bannerImage} 
                                alt={`${brandData?.name || domain} banner`}
                                className="banner-image"
                            />
                            <div className="banner-overlay" style={{
                                background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.7))`
                            }}></div>
                        </div>
                    )}
                    
                    <div 
                        className="brandfetch-header" 
                        style={{
                            ...(!bannerImage ? gradientStyle : {}),
                            position: 'relative',
                            zIndex: 5
                        }}
                    >
                        <div className="brand-logo-container">
                            {brandData?.logos && brandData.logos.length > 0 && brandData.logos[0].formats ? (
                                <img 
                                    src={brandData.logos[0].formats.find(format => format.src)?.src} 
                                    alt={`${brandData?.name || domain} logo`}
                                    className="brand-logo"
                                />
                            ) : (
                                <div className="fallback-logo">
                                    <Globe size={40} color="#666" />
                                </div>
                            )}
                        </div>
                        <div className="brand-info">
                            <h1>{brandData?.name || domain}</h1>
                            <p className="brand-domain">
                                <Globe size={16} />
                                <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">
                                    {domain}
                                    <ExternalLink size={14} />
                                </a>
                            </p>
                            {brandData?.description && <p className="brand-description">{brandData.description}</p>}
                        </div>
                        
                        {/* Add view toggle button */}
                        {onSwitchView && (
                            <button 
                                className="view-toggle-btn" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSwitchView();
                                }}
                                style={{
                                    position: 'absolute',
                                    top: '15px',
                                    right: '15px',
                                    background: 'rgba(255, 255, 255, 0.8)',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '6px 12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    color: '#333'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                    <ChevronDown size={14} />
                                </span>
                                Switch to Tabbed View
                            </button>
                        )}
                    </div>
                </div>
                
                {/* CONTENT SECTION - Clearly separated from header */}
                <div 
                    className="brandfetch-content"
                    style={{
                        marginTop: '20px', /* Explicit margin to separate from header */
                        borderTop: '1px solid #eee',
                        paddingTop: '20px'
                    }}
                >
                    {/* Brand Logos Section */}
                    {brandData?.logos && brandData.logos.length > 0 && (
                        <section className="brand-section">
                            <h2>Brand Logos</h2>
                            <div className="logo-grid">
                                {brandData.logos.map((logo, index) => (
                                    <div key={index} className="logo-item">
                                        {logo.formats && logo.formats.length > 0 && (
                                            <>
                                                <div className="logo-preview">
                                                    <img 
                                                        src={logo.formats.find(format => format.src)?.src} 
                                                        alt={`${brandData.name || domain} logo ${index + 1}`}
                                                    />
                                                </div>
                                                <div className="logo-details">
                                                    <span className="logo-type">{logo.type || 'Logo'}</span>
                                                    <span className="logo-format">
                                                        {logo.formats[0].format?.toUpperCase() || 'PNG'}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Brand Colors Section */}
                    {brandData.colors && brandData.colors.length > 0 && (
                        <section className="brand-section">
                            <h2>Brand Colors</h2>
                            <div className="colors-grid">
                                {brandData.colors.map((color, index) => (
                                    <div 
                                        key={index} 
                                        className="color-item"
                                        onClick={() => {
                                            navigator.clipboard.writeText(color.hex);
                                            setCopiedColor(color.hex);
                                            setTimeout(() => setCopiedColor(null), 2000);
                                        }}
                                    >
                                        <div 
                                            className="color-preview" 
                                            style={{ 
                                                backgroundColor: color.hex,
                                                color: getTextColor(color.hex)
                                            }}
                                        >
                                            {copiedColor === color.hex ? <Check size={18} /> : null}
                                        </div>
                                        <div className="color-details">
                                            <span className="color-hex">{color.hex}</span>
                                            {color.type && <span className="color-type">{color.type}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* AI Insights Section */}
                    {aiData && (
                        <section className="brand-section">
                            <h2>AI Insights</h2>
                            {insights && (
                                <div className="info-card">
                                    <h3>Brand Insights</h3>
                                    <div className="insights-list">
                                        {Array.isArray(insights) 
                                            ? insights.map((insight, index) => (
                                                <div key={index} className="insight-item">
                                                    <p>{insight}</p>
                                                </div>
                                            ))
                                            : typeof insights === 'object' && Object.keys(insights).length > 0
                                              ? Object.entries(insights).map(([key, value]) => (
                                                  <div key={key} className="insight-item">
                                                      <h4>{key.replace(/_/g, ' ').toUpperCase()}</h4>
                                                      <p>{value}</p>
                                                  </div>
                                                ))
                                              : <div className="insight-item">No insights available</div>
                                        }
                                    </div>
                                </div>
                            )}
                            
                            {recommendations && Array.isArray(recommendations) && recommendations.length > 0 && (
                                <div className="info-card">
                                    <h3>Recommendations</h3>
                                    <div className="recommendations-list">
                                        {recommendations.map((rec, index) => (
                                            <div key={index} className="recommendation-item">
                                                <p>{rec}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Brand Analysis Section */}
                    {brandAnalysis && (
                        <section className="brand-section">
                            <h2>Brand Analysis</h2>
                            <div className="info-card">
                                {typeof brandAnalysis === 'string' ? (
                                    <div className="brand-analysis-content">
                                        <p>{brandAnalysis}</p>
                                    </div>
                                ) : typeof brandAnalysis === 'object' ? (
                                    <div className="brand-analysis-content">
                                        {Object.entries(brandAnalysis).map(([key, value]) => (
                                            <div key={key} className="brand-analysis-item">
                                                <h4>{key.replace(/_/g, ' ').toUpperCase()}</h4>
                                                <p>{typeof value === 'object' ? JSON.stringify(value) : value}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p>No brand analysis available</p>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Social Links Section */}
                    {socialLinks && socialLinks.length > 0 && (
                        <section className="brand-section">
                            <h2>Social Profiles</h2>
                            <div className="social-links-grid">
                                {socialLinks.map((link, index) => {
                                    // Extract domain from URL to help identify the platform
                                    const url = new URL(link.url);
                                    const domain = url.hostname.replace('www.', '');
                                    
                                    // Determine platform type if not already specified
                                    const platformType = link.type || domain.split('.')[0];
                                    
                                    // Format the platform name
                                    const platformName = formatPlatformName(platformType);
                                    
                                    // Get platform color
                                    const platformColor = getSocialColor(platformType);
                                    
                                    // Extract username from URL if not provided
                                    let username = link.username;
                                    if (!username) {
                                        const pathParts = url.pathname.split('/').filter(part => part);
                                        if (pathParts.length > 0) {
                                            username = pathParts[pathParts.length - 1];
                                        }
                                    }
                                    
                                    // Get platform domain for display
                                    const platformDomain = domain;
                                    
                                    return (
                                        <a 
                                            key={index}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="social-link-item"
                                            style={{
                                                borderLeft: `4px solid ${platformColor}`
                                            }}
                                        >
                                            <div 
                                                className="social-icon-wrapper"
                                                style={{ 
                                                    backgroundColor: platformColor,
                                                    color: '#ffffff'
                                                }}
                                            >
                                                {getSocialIcon(platformType)}
                                            </div>
                                            <div className="social-details">
                                                <div className="social-platform-row">
                                                    <span className="social-platform">{platformName}</span>
                                                    {link.verified && (
                                                        <span className="social-verified">
                                                            <CheckCircle size={12} /> Verified
                                                        </span>
                                                    )}
                                                </div>
                                                {username && (
                                                    <span className="social-username">
                                                        @{username}
                                                    </span>
                                                )}
                                                {link.followers && (
                                                    <span className="social-followers">
                                                        {formatFollowers(link.followers)}
                                                    </span>
                                                )}
                                                <span className="social-url">
                                                    {platformDomain}
                                                </span>
                                            </div>
                                            <div className="social-action">
                                                <ExternalLink size={16} className="social-external-icon" />
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Company Information Section */}
                    {companyInfo && Object.keys(companyInfo).length > 0 && (
                        <section className="brand-section">
                            <h2>Company Information</h2>
                            <div className="company-info-grid">
                                {companyInfo.founded && (
                                    <div className="info-item">
                                        <div className="info-icon"><Award size={18} /></div>
                                        <div className="info-content">
                                            <span className="info-label">Founded</span>
                                            <span className="info-value">{companyInfo.founded}</span>
                                        </div>
                                    </div>
                                )}
                                {companyInfo.location && (
                                    <div className="info-item">
                                        <div className="info-icon"><MapPin size={18} /></div>
                                        <div className="info-content">
                                            <span className="info-label">Headquarters</span>
                                            <span className="info-value">{companyInfo.location}</span>
                                        </div>
                                    </div>
                                )}
                                {companyInfo.employees && (
                                    <div className="info-item">
                                        <div className="info-icon"><Briefcase size={18} /></div>
                                        <div className="info-content">
                                            <span className="info-label">Employees</span>
                                            <span className="info-value">{companyInfo.employees}</span>
                                        </div>
                                    </div>
                                )}
                                {companyInfo.revenue && (
                                    <div className="info-item">
                                        <div className="info-icon"><BarChart size={18} /></div>
                                        <div className="info-content">
                                            <span className="info-label">Revenue</span>
                                            <span className="info-value">{companyInfo.revenue}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Industries Section */}
                    {industries && industries.length > 0 && (
                        <section className="brand-section">
                            <h2>Industries</h2>
                            <div className="industries-grid">
                                {industries.map((industry, index) => (
                                    <div key={index} className="industry-item">
                                        <span className="industry-emoji">{industry.industry_emoji || '🏢'}</span>
                                        <span className="industry-name">{industry.industry || industry}</span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* App Ideas Section */}
                    {appIdeas && appIdeas.length > 0 && (
                        <section className="brand-section">
                            <h2>App Ideas</h2>
                            <div className="app-ideas-grid">
                                {appIdeas.map((idea, index) => {
                                    // Ensure idea is workable
                                    let processedIdea = idea;
                                    
                                    // If it's just a string, create a basic object
                                    if (typeof idea === 'string') {
                                        processedIdea = {
                                            headline: idea,
                                            description: ''
                                        };
                                    } 
                                    // Safety check to ensure the idea is an object
                                    else if (!idea || typeof idea !== 'object') {
                                        processedIdea = {
                                            headline: 'App Idea',
                                            description: ''
                                        };
                                    }
                                    
                                    // Now ensure all properties are strings
                                    const safeHeadline = ensureString(processedIdea.headline || 'App Idea');
                                    const safeDescription = ensureString(processedIdea.description || '');
                                    
                                    return (
                                        <div key={index} className="app-idea-item">
                                            <h3>{safeHeadline}</h3>
                                            {safeDescription && <p>{safeDescription}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Features Section */}
                    {features && features.length > 0 && (
                        <section className="brand-section">
                            <h2>Recommended Features</h2>
                            <div className="features-grid">
                                {features.map((feature, index) => {
                                    // Ensure feature is workable
                                    let processedFeature = feature;
                                    
                                    // If it's just a string, create a basic object
                                    if (typeof feature === 'string') {
                                        processedFeature = {
                                            name: feature,
                                            description: ''
                                        };
                                    } 
                                    // Safety check to ensure the feature is an object
                                    else if (!feature || typeof feature !== 'object') {
                                        processedFeature = {
                                            name: 'Feature',
                                            description: ''
                                        };
                                    }
                                    
                                    // Now ensure all properties are strings
                                    const safeName = ensureString(processedFeature.name || 'Feature');
                                    const safeDescription = ensureString(processedFeature.description || '');
                                    
                                    return (
                                        <div key={index} className="feature-item">
                                            <div className="feature-icon"><Zap size={18} /></div>
                                            <div className="feature-content">
                                                <h3>{safeName}</h3>
                                                {safeDescription && <p>{safeDescription}</p>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Marketing Tips Section */}
                    {marketingTips && marketingTips.length > 0 && (
                        <section className="brand-section">
                            <h2>Marketing Tips</h2>
                            <div className="marketing-tips-grid">
                                {marketingTips.map((tip, index) => {
                                    // First ensure tip is workable
                                    let processedTip = tip;
                                    
                                    // If it's just a string, create a basic object
                                    if (typeof tip === 'string') {
                                        processedTip = {
                                            category: 'General',
                                            text: tip
                                        };
                                    } 
                                    // Safety check to ensure the tip is an object
                                    else if (!tip || typeof tip !== 'object') {
                                        processedTip = {
                                            category: '',
                                            text: 'Unknown tip'
                                        };
                                    }
                                    
                                    // Now ensure all properties are strings
                                    const safeCategory = ensureString(processedTip.category || '');
                                    // Use tip_text if available, otherwise fall back to text property
                                    const safeText = ensureString(processedTip.tip_text || processedTip.text || '');
                                    
                                    return (
                                        <div key={index} className="marketing-tip-item">
                                            {safeCategory && (
                                                <span className="marketing-tip-category">{safeCategory}</span>
                                            )}
                                            <p>{safeText}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* Content Categories Section */}
                    {contentCategories && contentCategories.length > 0 && (
                        <section className="brand-section">
                            <h2>Content Categories</h2>
                            <div className="content-categories-grid">
                                {contentCategories.map((category, index) => {
                                    // Ensure category is workable
                                    let processedCategory = category;
                                    
                                    // If it's just a string, create a basic object
                                    if (typeof category === 'string') {
                                        processedCategory = {
                                            name: category,
                                            description: `Content related to ${category}`
                                        };
                                    } 
                                    // Safety check to ensure the category is an object
                                    else if (!category || typeof category !== 'object') {
                                        processedCategory = {
                                            name: 'Category',
                                            description: ''
                                        };
                                    }
                                    
                                    // Now ensure all properties are strings
                                    const safeName = ensureString(processedCategory.name || 'Category');
                                    const safeDescription = ensureString(processedCategory.description || '');
                                    
                                    return (
                                        <div key={index} className="content-category-item">
                                            <h3>{safeName}</h3>
                                            {safeDescription && <p>{safeDescription}</p>}
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    {/* App Links Section */}
                    {appLinks && appLinks.length > 0 && (
                        <section className="brand-section">
                            <h2>Mobile Apps</h2>
                            <div className="app-links-grid">
                                {appLinks.map((app, index) => (
                                    <a
                                        key={index}
                                        href={app.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="app-link-item"
                                    >
                                        <div className="app-icon">
                                            {getAppIcon(app.platform)}
                                        </div>
                                        <div className="app-details">
                                            <span className="app-platform">{app.platform}</span>
                                            {app.title && <span className="app-title">{app.title}</span>}
                                        </div>
                                        <ExternalLink size={14} className="app-external-icon" />
                                    </a>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Screenshots Section */}
                    {screenshots && Object.keys(screenshots).length > 0 && (
                        <section className="brand-section">
                            <h2>Screenshots</h2>
                            {Object.entries(screenshots).map(([type, images]) => (
                                <div key={type} className="screenshots-category">
                                    <h3>{type.charAt(0).toUpperCase() + type.slice(1)}</h3>
                                    <div className="screenshots-grid">
                                        {images.map((image, index) => (
                                            <div key={index} className="screenshot-item">
                                                <img src={image.src} alt={`${type} screenshot ${index + 1}`} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </section>
                    )}
                </div>

                <div className="brandfetch-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" onClick={handleRefresh}>Refresh Data</button>
                </div>
            </div>
        </div>
    );
};

export default BrandfetchSinglePageDisplay; 